import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { Building2, ChevronsUpDown, UserRound } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { useAppSelector } from 'hooks';
import { IOrg } from 'modules/orgs/interfaces';

type Option = { id: string; label: string; detail?: string; orgIds: string[] };

type Props = {
  orgs: IOrg[];
  selectedOrgIds: string[];
  onChange: (orgIds: string[]) => void;
};

// The picker lives inside an Ant Design Modal (z-index 1000); its portal must sit above it.
const POPOVER_CLASS = 'z-[1100] w-[360px] p-0';

function ShortcutPicker({
  label,
  icon,
  searchPlaceholder,
  emptyText,
  options,
  onPick,
}: {
  label: string;
  icon: React.ReactNode;
  searchPlaceholder: string;
  emptyText: string;
  options: Option[];
  onPick: (option: Option) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex-1 justify-between"
          disabled={options.length === 0}
        >
          <span className="flex items-center gap-2 truncate">
            {icon}
            {label}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={POPOVER_CLASS} align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={`${option.label} ${option.detail || ''} ${option.id}`}
                  onSelect={() => {
                    onPick(option);
                    setOpen(false);
                  }}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="min-w-0">
                    <span className="block truncate">{option.label}</span>
                    {option.detail && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {option.detail}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t('access.add.org_shortcuts_org_count', { count: option.orgIds.length })}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const byLabel = (a: Option, b: Option) =>
  a.label.localeCompare(b.label, 'da', { sensitivity: 'base', numeric: true });

/**
 * Admin shortcuts for the multi-org picker in AddMemberForm: add every org in a group,
 * or copy the orgs another user belongs to. Both only see orgs the admin can load, and
 * both add to the current selection so the admin can review it in the table before saving.
 */
export default function OrgSelectionShortcuts({ orgs, selectedOrgIds, onChange }: Props) {
  const { t } = useTranslation();
  const groupsMap = useAppSelector((state) => state.groups.groupsMap);

  const groupOptions = useMemo<Option[]>(() => {
    const byGroup = new Map<string, string[]>();
    orgs.forEach((org) => {
      if (!org.groupId) return;
      byGroup.set(org.groupId, [...(byGroup.get(org.groupId) || []), org.id]);
    });
    return Array.from(byGroup, ([id, orgIds]) => ({
      id,
      label: groupsMap?.[id]?.title || t('access.add.org_shortcuts_unknown_group', { id }),
      orgIds,
    })).sort(byLabel);
  }, [orgs, groupsMap, t]);

  const userOptions = useMemo<Option[]>(() => {
    const byUser = new Map<string, Option>();
    orgs.forEach((org) => {
      Object.entries(org.members || {}).forEach(([uid, member]) => {
        if (!member?.uid) return;
        const existing = byUser.get(uid);
        if (existing) {
          existing.orgIds.push(org.id);
          return;
        }
        const name = member.name?.trim();
        byUser.set(uid, {
          id: uid,
          label: name || member.email || uid,
          detail: name ? member.email : undefined,
          orgIds: [org.id],
        });
      });
    });
    return Array.from(byUser.values()).sort(byLabel);
  }, [orgs]);

  const addOrgs = (option: Option) => {
    const next = Array.from(new Set([...selectedOrgIds, ...option.orgIds]));
    const added = next.length - selectedOrgIds.length;
    onChange(next);
    if (added > 0) {
      message.success(
        t('access.add.org_shortcuts_added', { count: added, source: option.label })
      );
    } else {
      message.info(t('access.add.org_shortcuts_none_added', { source: option.label }));
    }
  };

  return (
    <div className="mb-3 flex flex-col gap-2 sm:flex-row">
      <ShortcutPicker
        label={t('access.add.org_shortcuts_group')}
        icon={<Building2 className="h-4 w-4" aria-hidden />}
        searchPlaceholder={t('access.add.org_shortcuts_group_search')}
        emptyText={t('access.add.org_shortcuts_group_empty')}
        options={groupOptions}
        onPick={addOrgs}
      />
      <ShortcutPicker
        label={t('access.add.org_shortcuts_user')}
        icon={<UserRound className="h-4 w-4" aria-hidden />}
        searchPlaceholder={t('access.add.org_shortcuts_user_search')}
        emptyText={t('access.add.org_shortcuts_user_empty')}
        options={userOptions}
        onPick={addOrgs}
      />
      <Button
        type="button"
        variant="ghost"
        onClick={() => onChange([])}
        disabled={selectedOrgIds.length === 0}
      >
        {t('access.add.org_shortcuts_clear')}
      </Button>
    </div>
  );
}
